import React, { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Archive, CalendarClock, ClipboardCheck, IdCard, Loader2, RotateCcw, Truck, User } from 'lucide-react';
import { apiClient, getApiErrorMessage } from '../../api/client';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import ConfirmModal from '../../components/ui/ConfirmModal';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { useTenantTimeZone } from '../../hooks/useTenantTimeZone';
import { queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import { fmtQty } from '../../lib/display-labels';
import { displayValue } from '../../features/fieldiq-handoff/contracts';
import { DhEmpty, DhPanel, DhSpec } from '../downhole/DownholeBits';
import type { TruckingRosterStatusResponse } from '../../types/trucking-roster';
import {
  DueBadge,
  StatusBadge,
  fleetStatusSkin,
  fmtDay,
  isServiceDue,
  useFleetUnits,
  vehicleOf,
} from '../../screens/trucking/FleetRegistryScreen';

/**
 * One fleet unit, /trucking/fleet/:id (#815: registers + a page per unit, in
 * place of the split pane). There is no GET /trucking/fleet/:id, so the page
 * reads the unit from the fleet list, retired units included — the same
 * request the register makes with "Show retired" on. Open to trucking and
 * surface rentals tenants alike (api#1041).
 */
export default function FleetUnitPage() {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const timeZone = useTenantTimeZone();
  const [retireOpen, setRetireOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const query = useFleetUnits(true);
  const failure = queryLoadFailure(query);
  const loading = query.isLoading && failure == null;
  const unit = failure == null ? (query.data ?? []).find((u) => u.id === id) ?? null : null;

  const retire = async () => {
    setBusy(true);
    try {
      await apiClient.delete<TruckingRosterStatusResponse>(`/trucking/fleet/${encodeURIComponent(id)}`);
      toast.success('Unit retired');
      queryClient.invalidateQueries({ queryKey: ['trucking-fleet'] });
      setRetireOpen(false);
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to retire unit'));
    } finally {
      setBusy(false);
    }
  };

  const reactivate = async () => {
    setBusy(true);
    try {
      await apiClient.post<TruckingRosterStatusResponse>(`/trucking/fleet/${encodeURIComponent(id)}/reactivate`);
      toast.success('Unit reactivated');
      queryClient.invalidateQueries({ queryKey: ['trucking-fleet'] });
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to reactivate unit'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="fleet-unit-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow={unit ? `Fleet · ${unit.type ?? '—'}` : 'Fleet'}
        title={unit ? unit.unit_number ?? '—' : loading ? 'Loading…' : 'Fleet unit'}
        onBack={() => navigate('/trucking/fleet')}
        backLabel="Back to Fleet"
        stats={
          !unit
            ? undefined
            : [
                { icon: <Truck className="size-4" aria-hidden="true" />, label: 'Status', sub: 'On the register', tone: 'neutral', value: fleetStatusSkin(unit.status).label },
                { icon: <CalendarClock className="size-4" aria-hidden="true" />, label: 'Next service', sub: isServiceDue(unit, timeZone) ? 'Date reached' : 'Scheduled', tone: isServiceDue(unit, timeZone) ? 'accent' : 'neutral', value: fmtDay(unit.next_service_date) },
                { icon: <IdCard className="size-4" aria-hidden="true" />, label: 'Plate expiry', sub: unit.plate ?? '—', tone: 'neutral', value: fmtDay(unit.plate_expiry) },
                { icon: <User className="size-4" aria-hidden="true" />, label: 'Driver', sub: 'Assigned', tone: 'info', value: unit.driver ?? '—' },
              ]
        }
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          {loading ? (
            <div className="flex justify-center py-16">
              <Loader2 className="size-6 animate-spin text-fieldiq-muted" aria-label="Loading unit" />
            </div>
          ) : !unit ? (
            <div className="rounded-fi-panel border border-fieldiq-border bg-fieldiq-card">
              {failure ? (
                <LoadFailureNotice error={queryFailureError(query)} kind={failure} subject="this unit" />
              ) : (
                <DhEmpty>This unit does not exist, or it was deleted.</DhEmpty>
              )}
            </div>
          ) : (
            <>
              <DhPanel eyebrow="Fleet" title="Unit record" testId="fleet-detail">
                <dl className="grid grid-cols-1 gap-x-6 gap-y-4 p-4 sm:grid-cols-2 md:grid-cols-4">
                  <DhSpec label="Status" value={<StatusBadge status={unit.status} />} />
                  <DhSpec label="Vehicle" value={vehicleOf(unit)} />
                  <DhSpec label="Type" value={unit.type ?? '—'} />
                  <DhSpec
                    label="Driver"
                    value={
                      unit.driver_id ? (
                        <Link className="underline underline-offset-2" to={`/trucking/drivers/${encodeURIComponent(unit.driver_id)}`}>
                          {unit.driver ?? 'Open driver'}
                        </Link>
                      ) : (
                        unit.driver ?? '—'
                      )
                    }
                  />
                  <DhSpec label="Plate" value={unit.plate ?? '—'} />
                  <DhSpec label="Plate expiry" value={fmtDay(unit.plate_expiry)} />
                  <DhSpec label="Jurisdiction" value={unit.jurisdiction ?? '—'} />
                  <DhSpec label="GVW" value={unit.gvw_kg == null ? '—' : `${fmtQty(unit.gvw_kg)} kg`} />
                  <DhSpec label="Last service" value={fmtDay(unit.last_service_date)} />
                  <DhSpec
                    label="Next service"
                    value={
                      <>
                        {fmtDay(unit.next_service_date)}
                        {isServiceDue(unit, timeZone) && <DueBadge />}
                      </>
                    }
                  />
                  <DhSpec label="Year" value={displayValue(unit.year)} />
                  <DhSpec label="VIN" mono value={unit.vin ?? '—'} />
                </dl>
                {unit.notes && <p className="whitespace-pre-line border-t border-fieldiq-border px-4 py-3 text-sm text-fieldiq-text">{unit.notes}</p>}
              </DhPanel>

              <div className="flex flex-wrap gap-2">
                {/* The unit's trip inspections — where its out-of-service defects are certified. */}
                <button
                  type="button"
                  onClick={() => navigate(`/trucking/trip-inspections?fleet_id=${encodeURIComponent(unit.id)}`)}
                  className="fi-action fi-action--standard border border-fieldiq-border bg-fieldiq-card text-fieldiq-text hover:bg-fieldiq-dark/50"
                >
                  <ClipboardCheck className="size-4" aria-hidden="true" /> Trip inspections
                </button>
                {unit.status === 'retired' ? (
                  <button type="button" disabled={busy} onClick={reactivate} className="fi-action fi-action--standard fi-action--primary disabled:opacity-50">
                    <RotateCcw className="size-4" aria-hidden="true" /> Reactivate unit
                  </button>
                ) : (
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => setRetireOpen(true)}
                    className="fi-action fi-action--standard border border-fieldiq-border bg-fieldiq-card text-fieldiq-text hover:bg-fieldiq-dark/50 disabled:opacity-50"
                  >
                    <Archive className="size-4" aria-hidden="true" /> Retire unit
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </section>

      <ConfirmModal
        isOpen={retireOpen}
        title={`Retire ${unit?.unit_number ?? 'unit'}?`}
        body={<>
          <p>This unit will be hidden from active fleet lists, dispatch, and scheduling. Its records stay. You can reactivate it later if needed.</p>
          {unit?.driver && <p className="mt-2 text-xs text-fieldiq-text">Assigned driver {unit.driver} will be released.</p>}
        </>}
        confirmLabel="Retire Unit"
        confirmVariant="destructive"
        loading={busy}
        onConfirm={retire}
        onCancel={() => setRetireOpen(false)}
      />
    </div>
  );
}
